"use client"

// The front door (rebuilt 2026-08-03). What changed and why:
//
// - It said "Deal Strategist — AI-powered deal analysis": stale branding from
//   an earlier incarnation of the app. A new hire told to "log into Setsail
//   OS" landed on a page naming a different product.
// - Google now comes FIRST. It is the real path for staff (only a handful of
//   accounts have passwords); with the form on top, new hires typed their
//   Google email + a guessed password and got "Invalid email or password"
//   before ever seeing the Google button.
// - Accessibility: the inputs were placeholder-only (no accessible name),
//   `outline-none` removed keyboard focus visibility with no replacement, and
//   the error banner was never announced to screen readers. Labels (sr-only),
//   focus-visible rings, role="alert" and autocomplete attributes fix all
//   four; placeholder contrast bumped from white/40 to white/60.

import { Suspense, useState, useEffect } from "react"
import { useSearchParams, useRouter } from "next/navigation"
import { signIn, useSession } from "next-auth/react"
import Image from "next/image"

const errorMessages: Record<string, string> = {
  Configuration: "Server configuration error — check environment variables.",
  // AccessDenied is what the sign-up domain lock (src/lib/auth-policy.ts)
  // returns for a NEW account outside setsail.ca. Say so, and say the fix.
  AccessDenied:
    "Sign-ups are limited to setsail.ca Google accounts. If you use another address, ask an admin to invite you first.",
  Verification: "Sign-in link expired. Try again.",
}

const INPUT_CLASS =
  "w-full rounded-lg px-4 py-3 text-sm text-white placeholder-white/60 outline-none ring-offset-0 focus-visible:ring-2 focus-visible:ring-emerald-400/80"

const INPUT_STYLE = {
  backgroundColor: "rgba(255, 255, 255, 0.08)",
  border: "1px solid rgba(255, 255, 255, 0.12)",
} as const

function LoginContent() {
  const searchParams = useSearchParams()
  const error = searchParams.get("error")
  // Honor ?callbackUrl= (used by the OAuth consent flow) but only same-site
  // relative paths — never an absolute/protocol-relative URL (open-redirect).
  const rawCb = searchParams.get("callbackUrl")
  const callbackUrl = rawCb && rawCb.startsWith("/") && !rawCb.startsWith("//") ? rawCb : "/"
  const { data: session, status } = useSession()
  const router = useRouter()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [credentialError, setCredentialError] = useState<string | null>(null)

  useEffect(() => {
    if (status === "authenticated") {
      router.replace(callbackUrl)
    }
  }, [status, router, callbackUrl])

  if (status === "loading" || status === "authenticated") {
    return null
  }

  const errorMessage = error
    ? errorMessages[error] || "Something went wrong. Please try again."
    : null

  async function handleCredentialLogin(e: React.FormEvent) {
    e.preventDefault()
    setIsLoading(true)
    setCredentialError(null)

    const result = await signIn("credentials", {
      email,
      password,
      redirect: false,
    })

    if (result?.error) {
      setCredentialError("Invalid email or password")
      setIsLoading(false)
    } else {
      router.replace(callbackUrl)
    }
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden" style={{ backgroundColor: "#01230b" }}>
      {/* Background glow */}
      <div
        className="pointer-events-none absolute"
        style={{
          width: 600,
          height: 600,
          borderRadius: 9999,
          backgroundColor: "rgba(21,142,29,0.10)",
          filter: "blur(120px)",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      />

      {/* Logo */}
      <Image
        src="/images/setsail-logo-dark.png"
        alt="SetSail"
        width={200}
        height={50}
        priority
        className="relative z-10 mb-8"
        style={{ filter: "brightness(0) invert(1)" }}
      />

      {/* Card wrapper */}
      <div className="relative z-10 w-full max-w-[24rem] px-6">
        <div
          className="space-y-6 rounded-2xl p-8"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.07)",
            backdropFilter: "blur(4px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          {/* Heading */}
          <div className="text-center">
            <h1 className="text-lg font-semibold text-white">Setsail OS</h1>
            <p className="mt-1 text-sm" style={{ color: "rgba(255, 255, 255, 0.6)" }}>
              Sign in with your setsail.ca Google account
            </p>
          </div>

          {/* Error messages — role="alert" so screen readers announce them. */}
          {(errorMessage || credentialError) && (
            <div
              role="alert"
              className="rounded-lg px-4 py-3 text-center text-sm"
              style={{
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.2)",
                color: "#f87171",
              }}
            >
              {credentialError || errorMessage}
            </div>
          )}

          {/* Google — the primary path for staff. */}
          <button
            onClick={() => signIn("google", { callbackUrl })}
            className="flex w-full cursor-pointer items-center justify-center gap-3 rounded-full bg-white px-4 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-emerald-400/80"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Sign in with Google
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4">
            <div className="h-px flex-1" style={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }} />
            <span className="text-xs" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
              or with email &amp; password
            </span>
            <div className="h-px flex-1" style={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }} />
          </div>

          {/* Email/password form — the secondary path (service + password
              accounts). Labels are sr-only: visually the design keeps
              placeholder styling, but every field has an accessible name. */}
          <form onSubmit={handleCredentialLogin} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="login-email" className="sr-only">
                Email
              </label>
              <input
                id="login-email"
                type="email"
                placeholder="Email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={INPUT_CLASS}
                style={INPUT_STYLE}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="login-password" className="sr-only">
                Password
              </label>
              <input
                id="login-password"
                type="password"
                placeholder="Password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={INPUT_CLASS}
                style={INPUT_STYLE}
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full cursor-pointer rounded-full px-4 py-3 font-medium text-white transition-colors disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-emerald-400/80"
              style={{ backgroundColor: "rgba(21,142,29,0.6)" }}
            >
              {isLoading ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>

        {/* Privacy + Terms — required for Google OAuth verification.
            The OAuth Consent Screen lists these URLs; Google's verification
            crawler also expects the homepage (https://os.setsail.ca/) to
            link to the privacy policy. Anonymous traffic to / redirects
            here, so this footer satisfies that check. */}
        <div
          className="mt-6 flex items-center justify-center gap-4 text-xs"
          style={{ color: "rgba(255, 255, 255, 0.4)" }}
        >
          <a
            href="https://www.setsail.ca/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-white/70"
          >
            Privacy Policy
          </a>
          <span style={{ color: "rgba(255, 255, 255, 0.2)" }}>·</span>
          <a
            href="https://www.setsail.ca/terms-conditions"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-white/70"
          >
            Terms of Service
          </a>
        </div>
      </div>
    </div>
  )
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginContent />
    </Suspense>
  )
}
